'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/transformers'

const TransformerBlockDiagram = dynamic(() => import('../_components/widgets/TransformerWidgets').then(m => m.TransformerBlockDiagram), { ssr: false })
const PositionalHeatmap = dynamic(() => import('../_components/widgets/TransformerWidgets').then(m => m.PositionalHeatmap), { ssr: false })

export default function TransformerBlock() {
  return (
    <Lesson>
      <Step gate="q-order">
        <H>Attention doesn’t know about order</H>
        <P>
          Here’s a surprise: self-attention treats its input as a <em>set</em>. Shuffle the tokens and every token gets the same
          mixture, just shuffled. “Dog bites man” and “man bites dog” would look identical. So transformers add a{' '}
          <strong>position</strong> vector to each token’s embedding before anything else.
        </P>
        <P>
          The original transformer used fixed waves of different frequencies (each position gets a unique pattern, like the hands
          of a clock); many models instead simply <em>learn</em> a position embedding table, just like the token embeddings. Our
          tiny GPT does the latter.
        </P>
        <Figure n="9.4" caption="Sinusoidal positional encodings. Each row (position) has a unique fingerprint of waves.">
          <PositionalHeatmap />
        </Figure>
        <Quiz
          id="q-order"
          question="Why do transformers need positional information added to the embeddings?"
          options={[
            { label: 'Attention itself is blind to order — without positions, any shuffle of the words looks the same.', correct: true },
            { label: 'To make the vectors longer.', why: 'Position vectors are added, not appended; the size doesn’t change.' },
            { label: 'To speed up training.', why: 'It’s about meaning — word order changes what a sentence says.' },
          ]}
        />
      </Step>

      <Step>
        <H>The block</H>
        <P>
          A transformer is a stack of identical <strong>blocks</strong>. Each block has two parts: attention (tokens share
          information) and an MLP (each token thinks on its own). Each part is wrapped in two tricks that make deep stacks
          trainable — <strong>layer normalisation</strong> and a <strong>residual connection</strong>.
        </P>
        <Figure n="9.5" wide caption="One transformer block. The thick line is the residual stream: each sub-layer reads from it and adds its result back.">
          <TransformerBlockDiagram />
        </Figure>
        <MB>{'\\mathbf{x} \\leftarrow \\mathbf{x} + \\text{Attention}(\\text{LN}(\\mathbf{x})) \\qquad \\mathbf{x} \\leftarrow \\mathbf{x} + \\text{MLP}(\\text{LN}(\\mathbf{x}))'}</MB>
      </Step>

      <Step gate="q-residual">
        <H>Why residual connections matter</H>
        <P>
          Remember the vanishing-gradient problem from the chain rule lesson? Multiply many small slopes together and the
          gradient dies before reaching the early layers. A residual connection computes <M>{'\\mathbf{x} + f(\\mathbf{x})'}</M>{' '}
          instead of <M>{'f(\\mathbf{x})'}</M>. Its derivative is <M>{"1 + f'(\\mathbf{x})"}</M> — there’s always a “1” path,
          so gradients flow straight down the stack, unharmed, no matter how deep it is.
        </P>
        <Quiz
          id="q-residual"
          question="A block’s sub-layers start out outputting nearly zero. With residual connections, what does the whole block do at the start of training?"
          options={[
            { label: 'Passes its input through almost unchanged — the stack starts as a near-identity and learns to add useful refinements.', correct: true },
            { label: 'Outputs zeros, blocking all information.', why: 'x + 0 = x: the residual path carries the input through.' },
            { label: 'Doubles its input.', why: 'Only if the sub-layer output a copy of x. Near zero adds nearly nothing.' },
          ]}
          explain="This is why our tiny GPT initialises its output projections small: each block starts as a gentle tweak to the residual stream."
        />
      </Step>

      <Step gate="layer-norm">
        <H>Build the pieces</H>
        <Exercise
          id="layer-norm"
          title="layerNorm and residual"
          {...EX.layerNorm}
          hints={[
            <>
              <C>const mean = vec.mean(x)</C>; variance is the mean of <C>(v - mean) ** 2</C>.
            </>,
            <>
              Residual: <C>vec.add(x, f(x))</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          That’s the complete architecture. GPT-2, GPT-4-class models, Llama, Claude: a token embedding, a stack of these blocks
          (dozens to over a hundred), and a final layer to next-token probabilities. The differences are mostly size, data and
          training details. Next: train one.
        </Callout>
      </Step>
    </Lesson>
  )
}
